import { WorkflowRunState } from '@smartsheet-bridge/bridge-sdk/es/http';
import { isNil } from 'ramda';
import { UIWorkflowRunState } from '../services/ducks/workflowDuck/workflowDuck';
import {
  AnyRunStepPayload,
  GroupRunStepPayload,
  JunctionRunStepPayload,
  ModuleRunStepPayload,
  StateRunStepPayload,
} from '../services/ducks/workflowRunDuck/workflowRunDuck';
import {
  convertWorkflowRunStateToUI,
  getAllGroupNames,
  getGroupOutput,
  getTransitEntity,
  hasTransitEntity,
} from './filePathVisitorUtils';
import {
  ChildWorkflowNode,
  GroupWorkflowNode,
  JunctionWorkflowNode,
  ModuleWorkflowNode,
  TransitWorkflowNode,
} from './workflowTree';

type DispatchStepFn = (step: AnyRunStepPayload) => void;

interface CreateAddStepVisitorProps {
  currentTransit: string;
  workflowState: WorkflowRunState;
  dispatch: DispatchStepFn;
  errorMsg?: string;
}

const getDefaultTransitPayload = (
  node: TransitWorkflowNode
): Pick<StateRunStepPayload, 'id' | 'name' | 'type'> => ({
  id: node.id,
  name: `State: ${node.id}`,
  type: 'STATE',
});

const getDefaultJunctionPayload = (
  node: JunctionWorkflowNode
): Pick<JunctionRunStepPayload, 'id' | 'name' | 'type'> => ({
  id: node.id,
  name: `Junction: ${node.id}`,
  type: 'JUNCTION',
});

const getDefaultModulePayload = (
  node: ModuleWorkflowNode
): Pick<ModuleRunStepPayload, 'id' | 'name' | 'type'> => {
  const extensionName =
    (!isNil(node.meta.metadata) && node.meta.metadata.extension.displayName) ||
    'Unknown';

  return {
    id: node.id,
    name: `${extensionName}: ${node.meta.metadata.module.name}`,
    type: 'MODULE',
  };
};

const getDefaultGroupPayload = (
  node: GroupWorkflowNode
): Pick<GroupRunStepPayload, 'id' | 'name' | 'type'> => {
  const moduleNode = node.meta.next[0];
  return {
    id: (moduleNode !== undefined && moduleNode.id) || node.id,
    name: `Junction: ${node.id}`,
    type: 'GROUP',
  };
};

export const createAddStepVisitor = ({
  dispatch,
  workflowState,
  currentTransit,
  errorMsg,
}: CreateAddStepVisitorProps) => {
  const uiWorkflowState = convertWorkflowRunStateToUI(workflowState);

  const TransitStep = (node: TransitWorkflowNode) => {
    if (
      currentTransit !== node.id ||
      node.next.length > 0 ||
      uiWorkflowState === UIWorkflowRunState.DONE
    ) {
      dispatch({
        ...getDefaultTransitPayload(node),
        state: UIWorkflowRunState.DONE,
        output: hasTransitEntity(node) ? getTransitEntity(node) : undefined,
      });
    } else if (uiWorkflowState === UIWorkflowRunState.STOPPED_BY_ERROR) {
      dispatch({
        ...getDefaultTransitPayload(node),
        state: UIWorkflowRunState.STOPPED_BY_ERROR,
        error: errorMsg,
      });
    } else {
      dispatch({
        ...getDefaultTransitPayload(node),
        state: uiWorkflowState,
      });
    }
    return node;
  };

  const JunctionStep = (node: JunctionWorkflowNode) => {
    if (currentTransit !== node.id) {
      dispatch({
        ...getDefaultJunctionPayload(node),
        state: UIWorkflowRunState.DONE,
      });
    } else if (uiWorkflowState === UIWorkflowRunState.STOPPED_BY_ERROR) {
      dispatch({
        ...getDefaultJunctionPayload(node),
        state: UIWorkflowRunState.STOPPED_BY_ERROR,
        error: errorMsg,
      });
    } else {
      dispatch({
        ...getDefaultJunctionPayload(node),
        state: uiWorkflowState,
      });
    }
    return node;
  };

  const ModuleStep = (node: ModuleWorkflowNode) => {
    if (node.meta.isExecuted === true) {
      dispatch({
        ...getDefaultModulePayload(node),
        state: UIWorkflowRunState.DONE,
        output: !isNil(node.meta.metadata)
          ? node.meta.metadata.output
          : undefined,
      });
    } else if (uiWorkflowState === UIWorkflowRunState.STOPPED_BY_ERROR) {
      dispatch({
        ...getDefaultModulePayload(node),
        state: UIWorkflowRunState.STOPPED_BY_ERROR,
        error: errorMsg,
      });
    } else {
      dispatch({
        ...getDefaultModulePayload(node),
        state: uiWorkflowState,
      });
    }

    return node;
  };

  const ChildStep = (node: ChildWorkflowNode) => {
    if (node.id == currentTransit && errorMsg) {
      dispatch({
        id: node.id,
        name: `Child Workflow: ${node.id}`,
        state: UIWorkflowRunState.STOPPED_BY_ERROR,
        type: 'CHILD',
        error: errorMsg,
      });
    } else {
      dispatch({
        id: node.id,
        name: `Child Workflow: ${node.id}`,
        state:
          node.id === currentTransit
            ? uiWorkflowState
            : UIWorkflowRunState.DONE,
        type: 'CHILD',
      });
    }
    return node;
  };

  const GroupStep = (node: GroupWorkflowNode) => {
    const allGroupNames = getAllGroupNames(node);

    if (
      !allGroupNames.includes(currentTransit) ||
      uiWorkflowState === UIWorkflowRunState.DONE
    ) {
      dispatch({
        ...getDefaultGroupPayload(node),
        state: UIWorkflowRunState.DONE,
        output: getGroupOutput(node),
      });
    } else if (uiWorkflowState === UIWorkflowRunState.STOPPED_BY_ERROR) {
      dispatch({
        ...getDefaultGroupPayload(node),
        state: UIWorkflowRunState.STOPPED_BY_ERROR,
        error: errorMsg,
      });
    } else {
      dispatch({
        ...getDefaultGroupPayload(node),
        state: uiWorkflowState,
      });
    }
    return node;
  };

  return {
    TransitStep,
    ModuleStep,
    JunctionStep,
    GroupStep,
    ChildStep,
  };
};
